/**
 * Created by mnaughton on 9/30/2015.
 */

/**
 * Front-end model representing a data item. See back-end complement for field descriptions.
 *
 * The following Field model attribute sets are located under the static `fields` attribute.
 * * Name
 * * Link
 * @module app/models/Feature
 */
import Backbone from "backbone";
import _ from "underscore";
import DocumentCollection from "app/models/DocumentCollection";
import ModelUtils from "@HBKEngineering/model-util";
import Geometry from "app/models/map/Geometry";
import FeatureData from "app/models/FeatureData";
import urlService from "../UrlService";
var Feature;

export default Feature = Backbone.Model.extend(
  {
    urlRoot: function () {
      var url;

      if (this.collection && this.collection.url) {
        url = _.result(this.collection, "url");
      } else {
        url = urlService.apiurl(
          "/features/bylayer/" + ModelUtils.idFor(this.get("dataType"))
        );
      }

      return url;
    },

    defaults: function () {
      return {
        geometry: null,
        properties: {},
      };
    },

    initialize: function (attributes, options) {
      Backbone.Model.prototype.initialize.apply(this, arguments);

      this.options = _.defaults({}, options);

      this.on(
        "change:geometry",
        function () {
          if (this._geometry) {
            this._geometry.set(this.get("geometry"), {
              silent: true,
            });
          }
        },
        this
      );

      this.on(
        "change:properties",
        function () {
          if (this._properties) {
            this._properties.set(this.get("properties"), {
              silent: true,
            });
          }
        },
        this
      );
    },

    /**
     * Gets the layer associated with this feature.
     * @return {MapLayerBase} The layer.
     */
    getLayer: function () {
      return this.originalLayer;
    },

    toJSON: function () {
      var json = Backbone.Model.prototype.toJSON.apply(this, arguments);

      delete json.documents;
      json.createdBy = ModelUtils.idFor(json.createdBy);

      return json;
    },

    /**
     * Gets the geometry object associated with this feature. Changes of the "geometry" attribute of this model reflect to the returned geometry model and vice versa.
     * @returns {app/models/map/Geometry} The geometry object.
     */
    getGeometry: function () {
      if (!this._geometry) {
        this._geometry = new Geometry(this.get("geometry"), {
          feature: this,
        });

        this._geometry.on("change", this._onGeometryModelChanged, this);
      }

      return this._geometry;
    },

    _onGeometryModelChanged: function () {
      this.set("geometry", this._geometry.toJSON(), {
        silent: true,
      });
    },

    /**
     * Sets the geometry object associated with this feature. Changes of the "geometry" attribute of this model reflect to the returned geometry model and vice versa.
     * @param {app/models/map/Geometry} geometry The geometry object.
     */
    setGeometry: function (geometry) {
      if (this._geometry) {
        this._geometry.off("change", this._onGeometryModelChanged, this);
      }

      this.set("geometry", geometry.toJSON());
      this._geometry = geometry;
      this._geometry.options.feature = this;
      this._geometry.on("change", this._onGeometryModelChanged, this);
    },

    /**
     * Gets the properties object associated with this feature, wrapped as a Backbone model. Changes of the "properties" attribute of this model reflect to the returned data model and vice versa.
     * @todo Make this use _onPropertiesModelChanged
     * @returns {app/models/FeatureData} The data object.
     */
    getProperties: function () {
      if (!this._properties) {
        /* Begin misplaced feature properties hotfix. See https://github.com/HBKEngineering/chartroom-client/issues/891*/
        var misplacedKeys = _.filter(_.keys(this.attributes), function (
          attrKey
        ) {
          return attrKey.length == 24;
        });

        if (misplacedKeys.length) {
          console.warn(
            "Misplaced attribute keys detected for feature " + this.id
          );
        }
        /* End misplaced feature properties hotfix*/
        this._properties = new FeatureData(this.get("properties"), {
          feature: this,
        });

        this._properties.on(
          "change",
          function () {
            this.set("properties", this._properties.toJSON(), {
              silent: true,
            });
          },
          this
        );
      }

      return this._properties;
    },

    /**
     * Gets the attribute associated with the feature. If the value isn't found in the attributes section, looks through the attributes.properties section where data is stored sometimes.
     * @param {string} attribute The string of the attribute to look for..
     */
    get: function (attribute) {
      var result = Backbone.Model.prototype.get.apply(this, arguments);

      if (_.isUndefined(result)) {
        //If result is undefined, it hasn't been found with super method. Look in the properties section.
        //Return the value of the properties hash that corresponds with the attribute key.
        //A miss will return undefined if not found, same as backbone.model.get.
        if (this.attributes && _.isObject(this.attributes.properties)) {
          return this.attributes.properties[attribute];
        }
      }

      return result;
    },

    /**
     * Sets the properties object associated with this feature. Changes of the "properties" attribute of this model reflect to the returned geometry model and vice versa.
     * @param {app/models/FeatureData|Object} properties The property object.
     */
    setProperties: function (properties) {
      if (this._properties) {
        this._properties.off("change", this._onPropertiesModelChanged, this);
      }

      if (properties) {
        if (!_.has(properties, "attributes")) {
          properties = new FeatureData(properties, {
            feature: this,
          });
        }
      } else {
        properties = new FeatureData(
          {},
          {
            feature: this,
          }
        );
      }

      this.set("properties", properties.toJSON());
      this._properties = properties;
      this._properties.options.feature = this;
      this._properties.on("change", this._onPropertiesModelChanged, this);
    },

    /**
     * Gets a reference to this feature.
     * @return {app/models/Feature} The associated feature.
     */
    getFeature: function () {
      return this;
    },

    /**
     * Gets the id of this feature.
     * @returns {*} The feature id
     */
    featureId: function () {
      return this.id;
    },

    _onPropertiesModelChanged: function () {
      this.set("properties", this._properties.toJSON(), {
        silent: true,
      });
    },

    /**
     * Gets the collection of documents associated with this feature.
     * @return {app/models/DocumentCollection} The document collection.
     */
    getDocuments: function () {
      if (!this._documents && !this.isNew()) {
        this._documents = Feature.getDocuments(this.id);
      }

      return this._documents;
    },

    /**
     * Gets a value indicating whether or not this feature is external.
     */
    isExternal: function () {
      return false;
    },
  },
  {
    /**
     * Gets the collection of documents associated with the feature with the given ID.
     * @param {String} featureId The ID of the feature for which to retrieve a document collection
     * @return {app/models/DocumentCollection} The document collection.
     */
    getDocuments: function (featureId) {
      return new DocumentCollection(null, {
        url: Feature.getDocumentsUrl(featureId),
      });
    },

    /**
     * Gets the API endpoint for working with documents associated with the given feature.
     * @param {String} featureId The ID of the feature for which to retrieve a document collection
     * @returns {String} The URL of the endpoint.
     */
    getDocumentsUrl: function (featureId) {
      return urlService.apiurl("/documents/byfeature/" + featureId);
    },

    /**
     * Gets a link to the file attached to the given document associated with the given feature.
     * @param {String} featureId The ID of the feature containing the file to link to
     * @param {app/models/Document} fileModel The document to link to
     * @param {String} token The authentication token used to access the file
     */
    getDocumentFilePath: function (featureId, fileModel, token) {
      return (
        urlService.apiurl(
          "/documents/byfeature/" +
            featureId +
            "/" +
            fileModel.id +
            "/file/" +
            fileModel.getFileName()
        ) +
        "?token=" +
        token
      );
    },

    /**
     * Generates a feature from the provided geometry and properties models.
     * @param {app/models/map/Geometry} geometry The geometry to incorporate into the feature.
     * @param {app/models/FeatureData|Object} [properties] The properties data to incorporate into the feature.
     * @param {object} options Some options to pass to the constructor of Feature.
     * @param {app/models/Layer} [options.featureLayer] The layer to be associated with the feature, if any.
     * @param [options.urlBuilder] The URL builder to pass to the feature model. Only used for persisting models.
     */
    generate: function (geometry, properties, options) {
      if (!options) {
        options = properties;
        properties = null;
      }

      var feature = new Feature(
        {
          dataType: options.featureLayer ? options.featureLayer.id : void 0,
        },
        options
      );

      feature.setGeometry(geometry);
      feature.setProperties(properties);

      return feature;
    },
  }
);



//////////////////
// WEBPACK FOOTER
// ./public/js/app/models/Feature.js
// module id = 87
// module chunks = 1